/**
 * How Clerk's own widgets (sign-in, the workspace switcher, the user button) take the app's look.
 *
 * Clerk's styles define CSS variables of their own on its elements, among them `--accent` and `--border`. A Clerk
 * variable set to `var(--accent)` therefore names itself, which is invalid: from clerk-js 5.128 the sign-in's
 * primary button painted `background-color: var(--accent)` and came out transparent (white text on white). So every
 * colour handed to Clerk is one of our variables Clerk does not define: the accent goes as `--primary`
 * (ThemeProvider sets it to the accent on every page, signed out included).
 */
import type { ComponentProps } from 'react';
import type { ClerkProvider, OrganizationSwitcher } from '@clerk/clerk-react';

type Appearance = NonNullable<ComponentProps<typeof ClerkProvider>['appearance']>;
type WidgetAppearance = NonNullable<ComponentProps<typeof OrganizationSwitcher>['appearance']>;

/** The clerk-js the app was checked against: Clerk's CDN otherwise serves its newest 5.x, which changed under us once. */
export const CLERK_JS_VERSION = '5.128.0';

/** Variables Clerk defines on its own elements; a colour handed to Clerk must never be `var()` of one of these. */
export const CLERK_OWN_VARIABLES = ['--accent', '--accentContrast', '--accentHover', '--alpha', '--border'];

export const CLERK_APPEARANCE: Appearance = {
  variables: {
    colorPrimary: 'var(--primary)',
    colorBackground: 'var(--surface)',
    colorText: 'var(--ink)',
    colorTextSecondary: 'var(--ink-2)',
    colorInputBackground: 'var(--surface2)',
    colorInputText: 'var(--ink)',
    colorDanger: 'var(--bad)',
    colorNeutral: 'var(--ink-2)',
    borderRadius: '8px',
  },
};

const card = { borderRadius: '10px', border: '1px solid var(--line)', boxShadow: '0 1px 2px rgba(16,16,24,.04), 0 12px 32px rgba(16,16,24,.10)' };
const quiet = { filter: 'grayscale(1)', opacity: 0.8 };
const row = { color: 'var(--ink-2)', '&:hover': { backgroundColor: 'var(--side-hover)', color: 'var(--ink)' } };

/** The simplified sidebar's switcher and user button (#1500): neutral avatars, ink text, the sidebar's hover, a flat card. */
export const SIMPLE_CLERK_WIDGETS: WidgetAppearance = {
  variables: { fontFamily: 'inherit', fontSize: '13px', borderRadius: '7px' },
  elements: {
    organizationSwitcherTrigger: { padding: '4px 8px 4px 4px', borderRadius: '7px', color: 'var(--ink)', boxShadow: 'none',
      '&:hover': { backgroundColor: 'var(--side-hover)' }, '&:focus': { boxShadow: 'none' } },
    organizationSwitcherTriggerIcon: { color: 'var(--ink-3)' },
    organizationPreviewMainIdentifier: { color: 'var(--ink)', fontWeight: 500 },
    organizationPreviewSecondaryIdentifier: { color: 'var(--ink-3)' },
    organizationPreviewAvatarBox: { width: '22px', height: '22px', borderRadius: '6px', ...quiet },
    userButtonAvatarBox: { width: '24px', height: '24px', ...quiet },
    userPreviewAvatarBox: quiet,
    avatarBox: quiet,
    organizationSwitcherPopoverCard: card,
    userButtonPopoverCard: card,
    organizationSwitcherPreviewButton: { '&:hover': { backgroundColor: 'var(--side-hover)' } },
    organizationSwitcherPopoverActionButton: row,
    organizationSwitcherPopoverActionButtonIconBox: { color: 'var(--ink-3)' },
    userButtonPopoverActionButton: row,
    organizationSwitcherPopoverFooter: { background: 'var(--sunk)' },
    userButtonPopoverFooter: { background: 'var(--sunk)' },
  },
};
